import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { Card, CardContent, CardHeader } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import axios from 'axios';
import { KeyRound, CheckCircle2, XCircle, Loader2, LogIn, Eye, EyeOff } from 'lucide-react';
import { Seo } from '../components/seo/Seo';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Nanouk Images
const NANOUK = {
    happy: 'https://images.unsplash.com/photo-1767381604151-bae00f2fb337?w=200&h=200&fit=crop',
    sad: 'https://images.unsplash.com/photo-1767381604151-bae00f2fb337?w=200&h=200&fit=crop',
};

export default function ResetPassword() {
    const { language } = useLanguage();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const token = searchParams.get('token');

    const [loading, setLoading] = useState(true);
    const [tokenValid, setTokenValid] = useState(false);
    const [email, setEmail] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [resetting, setResetting] = useState(false);
    const [success, setSuccess] = useState(false);

    useEffect(() => {
        if (token) {
            verifyToken();
        } else {
            setLoading(false);
        }
    }, [token]);

    const verifyToken = async () => {
        try {
            const response = await axios.get(`${API}/auth/verify-reset-token?token=${token}`);
            setTokenValid(response.data.valid);
            setEmail(response.data.email);
        } catch (error) {
            setTokenValid(false);
        } finally {
            setLoading(false);
        }
    };

    const handleResetPassword = async (e) => {
        e.preventDefault();
        
        if (newPassword !== confirmPassword) {
            toast.error(language === 'de' ? 'Passwörter stimmen nicht überein' : 'Passwords do not match');
            return;
        }
        
        if (newPassword.length < 6) {
            toast.error(language === 'de' ? 'Passwort muss mindestens 6 Zeichen haben' : 'Password must be at least 6 characters');
            return;
        }

        setResetting(true);
        try {
            await axios.post(`${API}/auth/reset-password`, {
                token,
                new_password: newPassword
            });
            setSuccess(true);
            toast.success(language === 'de' ? 'Passwort erfolgreich geändert!' : 'Password changed successfully!');
        } catch (error) {
            toast.error(error.response?.data?.detail || (language === 'de' ? 'Fehler beim Zurücksetzen' : 'Reset failed'));
        } finally {
            setResetting(false);
        }
    };

    // Loading State
    if (loading) {
        return (
            <div className="min-h-screen bg-background flex items-center justify-center p-4">
                <Card className="bg-secondary border-border w-full max-w-md">
                    <CardContent className="p-8 text-center">
                        <Loader2 className="w-12 h-12 text-cyan-500 animate-spin mx-auto mb-4" />
                        <p className="text-muted-foreground">
                            {language === 'de' ? 'Link wird überprüft...' : 'Verifying link...'}
                        </p>
                    </CardContent>
                </Card>
            </div>
        );
    }

    // No Token or Invalid Token
    if (!token || !tokenValid) {
        return (
            <div className="min-h-screen bg-background flex items-center justify-center p-4">
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="w-full max-w-md"
                >
                    <Card className="bg-secondary border-border">
                        <CardContent className="p-8 text-center">
                            <div className="w-20 h-20 mx-auto mb-4 rounded-full overflow-hidden border-2 border-red-500/50">
                                <img src={NANOUK.sad} alt="Nanouk sad" className="w-full h-full object-cover" />
                            </div>
                            <div className="w-16 h-16 mx-auto bg-red-500/20 rounded-full flex items-center justify-center mb-4">
                                <XCircle className="w-8 h-8 text-red-500" />
                            </div>
                            <h2 className="text-xl font-bold text-foreground mb-2">
                                {language === 'de' ? 'Link ungültig oder abgelaufen' : 'Invalid or Expired Link'}
                            </h2>
                            <p className="text-muted-foreground text-sm mb-6">
                                {language === 'de'
                                    ? 'Dieser Reset-Link ist nicht mehr gültig. Bitte fordere einen neuen an.'
                                    : 'This reset link is no longer valid. Please request a new one.'}
                            </p>
                            <Button
                                onClick={() => navigate('/auth')}
                                className="w-full bg-primary text-primary-foreground hover:bg-primary/90"
                            >
                                {language === 'de' ? 'Zurück zur Anmeldung' : 'Back to Login'}
                            </Button>
                        </CardContent>
                    </Card>
                </motion.div>
            </div>
        );
    }

    // Success State
    if (success) {
        return (
            <div className="min-h-screen bg-background flex items-center justify-center p-4">
                <motion.div
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className="w-full max-w-md"
                >
                    <Card className="bg-secondary border-border">
                        <CardContent className="p-8 text-center">
                            <div className="w-20 h-20 mx-auto mb-4 rounded-full overflow-hidden border-2 border-green-500/50">
                                <img src={NANOUK.happy} alt="Nanouk happy" className="w-full h-full object-cover" />
                            </div>
                            <div className="w-16 h-16 mx-auto bg-green-500/20 rounded-full flex items-center justify-center mb-4">
                                <CheckCircle2 className="w-8 h-8 text-green-500" />
                            </div>
                            <h2 className="text-xl font-bold text-foreground mb-2">
                                {language === 'de' ? 'Passwort geändert!' : 'Password Changed!'}
                            </h2>
                            <p className="text-muted-foreground text-sm mb-6">
                                {language === 'de'
                                    ? 'Dein Passwort wurde erfolgreich zurückgesetzt. Du kannst dich jetzt anmelden.'
                                    : 'Your password has been successfully reset. You can now log in.'}
                            </p>
                            <Button
                                onClick={() => navigate('/auth')}
                                className="w-full bg-primary text-primary-foreground hover:bg-primary/90"
                            >
                                <LogIn className="w-4 h-4 mr-2" />
                                {language === 'de' ? 'Zur Anmeldung' : 'Go to Login'}
                            </Button>
                        </CardContent>
                    </Card>
                </motion.div>
            </div>
        );
    }

    // Reset Password Form
    return (
        <div className="min-h-screen bg-background flex items-center justify-center p-4" data-testid="reset-password-page">
            <Seo title={language === 'de' ? 'Passwort zurücksetzen' : 'Reset Password'} path="/reset-password" language={language} noindex />
            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="w-full max-w-md"
            >
                <Card className="bg-secondary border-border">
                    <CardHeader className="pb-2">
                        <div className="flex items-center justify-center gap-3 mb-4">
                            <div className="w-12 h-12 rounded-xl overflow-hidden">
                                <img src={NANOUK.happy} alt="Nanouk" className="w-full h-full object-cover" />
                            </div>
                            <div>
                                <h2 className="font-bold text-xl text-foreground">
                                    {language === 'de' ? 'Neues Passwort' : 'New Password'}
                                </h2>
                                <p className="text-xs text-muted-foreground">{email}</p>
                            </div>
                        </div>
                    </CardHeader>

                    <CardContent className="pt-4">
                        <form onSubmit={handleResetPassword} className="space-y-4">
                            <p className="text-sm text-muted-foreground text-center mb-4">
                                {language === 'de'
                                    ? 'Wähle ein neues Passwort für dein Konto.'
                                    : 'Choose a new password for your account.'}
                            </p>
                            
                            <div className="space-y-2">
                                <Label htmlFor="new-password" className="text-muted-foreground">
                                    {language === 'de' ? 'Neues Passwort' : 'New Password'}
                                </Label>
                                <div className="relative">
                                    <Input
                                        id="new-password"
                                        type={showPassword ? 'text' : 'password'}
                                        value={newPassword}
                                        onChange={(e) => setNewPassword(e.target.value)}
                                        placeholder={language === 'de' ? 'Min. 6 Zeichen' : 'Min. 6 characters'}
                                        required
                                        className="bg-muted/50 border-border text-foreground pr-10"
                                        data-testid="new-password-input"
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setShowPassword(!showPassword)}
                                        className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                                    >
                                        {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                                    </button>
                                </div>
                            </div>

                            <div className="space-y-2">
                                <Label htmlFor="confirm-password" className="text-muted-foreground">
                                    {language === 'de' ? 'Passwort bestätigen' : 'Confirm Password'}
                                </Label>
                                <Input
                                    id="confirm-password"
                                    type={showPassword ? 'text' : 'password'}
                                    value={confirmPassword}
                                    onChange={(e) => setConfirmPassword(e.target.value)}
                                    placeholder={language === 'de' ? 'Passwort wiederholen' : 'Repeat password'}
                                    required
                                    className="bg-muted/50 border-border text-foreground"
                                    data-testid="confirm-password-input"
                                />
                            </div>

                            {/* Password strength indicator */}
                            {newPassword.length > 0 && (
                                <div className="space-y-1">
                                    <div className="flex gap-1">
                                        {[1, 2, 3, 4].map((i) => (
                                            <div
                                                key={i}
                                                className={`h-1 flex-1 rounded ${
                                                    newPassword.length >= i * 3
                                                        ? newPassword.length >= 12
                                                            ? 'bg-green-500'
                                                            : newPassword.length >= 8
                                                            ? 'bg-yellow-500'
                                                            : 'bg-red-500'
                                                        : 'bg-muted'
                                                }`}
                                            />
                                        ))}
                                    </div>
                                    <p className="text-xs text-muted-foreground">
                                        {newPassword.length < 6
                                            ? (language === 'de' ? 'Zu kurz' : 'Too short')
                                            : newPassword.length < 8
                                            ? (language === 'de' ? 'Schwach' : 'Weak')
                                            : newPassword.length < 12
                                            ? (language === 'de' ? 'Mittel' : 'Medium')
                                            : (language === 'de' ? 'Stark' : 'Strong')}
                                    </p>
                                </div>
                            )}

                            <Button
                                type="submit"
                                className="w-full bg-primary text-primary-foreground hover:bg-primary/90"
                                disabled={resetting}
                                data-testid="reset-submit-btn"
                            >
                                {resetting ? (
                                    <>
                                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                        {language === 'de' ? 'Wird gespeichert...' : 'Saving...'}
                                    </>
                                ) : (
                                    <>
                                        <KeyRound className="w-4 h-4 mr-2" />
                                        {language === 'de' ? 'Passwort ändern' : 'Change Password'}
                                    </>
                                )}
                            </Button>
                        </form>
                    </CardContent>
                </Card>
            </motion.div>
        </div>
    );
}
